import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../AuthContext";
import { LoadingContext } from "../LoadingContext";
import Loading from "./Loading";
import "./AdminInquiries.css";
import Swal from "sweetalert2";
import { DateRangePicker } from 'react-date-range';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { toLocalDateString } from "../dateUtils";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const MySwal = Swal.mixin({
  customClass: {
    container: "my-swal-container",
  },
});

function AdminInquiries() {
  const getTodayDate = () => {
    const today = new Date();
    return toLocalDateString(today);
  };

  const { authFetch, userInfo } = useContext(AuthContext);
  const [inquiries, setInquiries] = useState([]);
  const [filteredInquiries, setFilteredInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [inquiriesPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [showPicker, setShowPicker] = useState(false);
  const [range, setRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection'
    }
  ]);
  const [statusFilter, setStatusFilter] = useState("new");
  const { setIsLoading } = useContext(LoadingContext);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [inquiryDetails, setInquiryDetails] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const formatFileSize = (bytes) => {
    if (!bytes) return "Unknown size";
    if (bytes < 1024) return bytes + " bytes";
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    else return (bytes / 1048576).toFixed(1) + " MB";
  };

  useEffect(() => {
    const styleEl = document.createElement("style");
    styleEl.textContent = `
      .my-swal-container {
        z-index: 20000 !important;
      }
    `;
    document.head.appendChild(styleEl);
    return () => {
      document.head.removeChild(styleEl);
    };
  }, []);

    const fetchInquiries = async () => {
      setIsLoading(true);
      setLoading(true);
      try {
        const response = await authFetch(
          `${API_BASE_URL}/Inquiries?status=${statusFilter}&start=${format(range[0].startDate, 'yyyy-MMM-dd')}&end=${format(range[0].endDate, 'yyyy-MMM-dd')}`
        );

        if (!response.ok) {
          if (response.status === 404) {
            setInquiries([]);
            setFilteredInquiries([]);
            setLoading(false);
            setIsLoading(false);
            return;
          } else {
            throw new Error('Failed to fetch inquiries');
          }
        }

        const data = await response.json();

        if (!data || data.length === 0) {
          setInquiries([]);
          setFilteredInquiries([]);
          setLoading(false);
          setIsLoading(false);
          return;
        }

        const sortedInquiries = data.sort((a, b) => {
          return new Date(b.submittedDate) - new Date(a.submittedDate);
        });

        // Apply client-side search filter
        let result = sortedInquiries;
        if (searchTerm) {
          const search = searchTerm.toLowerCase();
          result = result.filter(
            (inquiry) =>
              (inquiry.name && inquiry.name.toLowerCase().includes(search)) ||
              (inquiry.companyName &&
                inquiry.companyName.toLowerCase().includes(search))
          );
        }

        setInquiries(sortedInquiries);
        setFilteredInquiries(result);
      } catch (err) {
        setInquiries([]);
        setFilteredInquiries([]);
      } finally {
        setIsLoading(false);
        setLoading(false);
      }
    };

  useEffect(() => {
    fetchInquiries();
  }, [setIsLoading]);

  useEffect(() => {
    let result = [...inquiries];

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(
        (inquiry) =>
          (inquiry.name && inquiry.name.toLowerCase().includes(search)) ||
          (inquiry.companyName &&
            inquiry.companyName.toLowerCase().includes(search))
      );
    }

    setFilteredInquiries(result);
    setCurrentPage(1);
  }, [searchTerm, inquiries]);

  useEffect(() => {
    const fetchInquiryDetails = async () => {
      if (!selectedInquiry) return;

      setModalLoading(true);
      try {
        const response = await authFetch(
          `${API_BASE_URL}/Inquiries/${selectedInquiry}`
        );

        if (!response.ok) {
          throw new Error(
            `Failed to fetch inquiry: ${response.status} ${response.statusText}`
          );
        }

        const data = await response.json();

        if (!data) {
          throw new Error("No inquiry data returned from API");
        }

        let responses = [];
        try {
          const responsesResponse = await authFetch(
            `${API_BASE_URL}/api/InquiryResponses/inquiry/${selectedInquiry}`
          );
          if (responsesResponse.ok) {
            responses = await responsesResponse.json();
          }
        } catch (respError) {
          // Silent fail (previously only 1 console.error line in this catch block, no other actions)
        }

        let productName = null;
        try {
          if (data.productId) {
            const productResponse = await fetch(
              `${API_BASE_URL}/Products/${data.productId}`
            );
            if (productResponse.ok) {
              const productData = await productResponse.json();
              productName = productData.name || productData.title;
            }
          }
        } catch (prodError) {
          // Silent fail (previously only 1 console.error line in this catch block, no other actions)
        }

        const enhancedData = {
          ...data,
          status: data.status || "New",
          responses: responses || [],
          productName: productName,
        };

        setInquiryDetails(enhancedData);
      } catch (err) {
        // Silent fail (previously only 1 console.error line in this catch block, no other actions)
      } finally {
        setModalLoading(false);
      }
    };

    if (showModal && selectedInquiry) {
      fetchInquiryDetails();
    }
  }, [showModal, selectedInquiry]);

  const indexOfLastInquiry = currentPage * inquiriesPerPage;
  const indexOfFirstInquiry = indexOfLastInquiry - inquiriesPerPage;
  const currentInquiries = filteredInquiries.slice(
    indexOfFirstInquiry,
    indexOfLastInquiry
  );
  const totalPages = Math.ceil(filteredInquiries.length / inquiriesPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const applyFilters = () => {
    fetchInquiries();
  };

  const getStatusClass = (status) => {
    if (!status) return "";

    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case "new":
        return "admin-inquiry-status-new";
      case "closed":
        return "admin-inquiry-status-closed";
      default:
        return "";
    }
  };

 const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    } catch (e) {
      return dateString;
    }
  };

  const handleViewInquiry = (inquiryId) => {
    setSelectedInquiry(inquiryId);
    setTimeout(() => {
      setShowModal(true);
    }, 50);
  };

  const handleCloseModal = (e) => {
    if (e) e.stopPropagation();
    setShowModal(false);

    setTimeout(() => {
      setSelectedInquiry(null);
      setInquiryDetails(null);
      setResponseMessage("");
    }, 300);
  };

  const handleSubmitResponse = async (closeInquiry = false) => {
    if (!responseMessage.trim()) {
      MySwal.fire({
        title: "Warning!",
        text: "Please enter a remark message",
        icon: "warning",
        confirmButtonColor: "#45a049",
      });
      return;
    }

    if (!inquiryDetails) return;

    setUpdatingStatus(true);
    try {
      const responseData = {
        inquiryId: parseInt(inquiryDetails.id),
        message: responseMessage,
        respondedBy: userInfo?.username || "Admin",
      };

      const newStatus = closeInquiry ? "Closed" : "New";

      const responseResult = await authFetch(
        `${API_BASE_URL}/api/InquiryResponses`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Update-Status": newStatus,
          },
          body: JSON.stringify(responseData),
        }
      );

      if (!responseResult.ok) {
        throw new Error(`Failed to submit response: ${responseResult.status}`);
      }
      const inquiryResponse = await authFetch(
        `${API_BASE_URL}/Inquiries/${selectedInquiry}`
      );
      if (!inquiryResponse.ok) {
        throw new Error(`Failed to refresh inquiry: ${inquiryResponse.status}`);
      }
      const updatedInquiry = await inquiryResponse.json();
      const responsesResponse = await authFetch(
        `${API_BASE_URL}/api/InquiryResponses/inquiry/${selectedInquiry}`
      );
      if (responsesResponse.ok) {
        const responses = await responsesResponse.json();
        updatedInquiry.responses = responses;
      }

      setInquiryDetails(updatedInquiry);
      setResponseMessage("");

      const updatedInquiries = inquiries.map((inquiry) =>
        inquiry.id === selectedInquiry
          ? { ...inquiry, status: newStatus }
          : inquiry
      );
      setInquiries(updatedInquiries);

      MySwal.fire({
        title: "Success!",
        text: "Remark submitted successfully!",
        icon: "success",
        timer: 1500,
        confirmButtonColor: "#45a049",
      });
    } catch (err) {
      MySwal.fire({
        title: "Error!",
        text: "Failed to submit response. Please try again.",
        icon: "error",
        confirmButtonColor: "#45a049",
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-inquiry-container">
        <div className="admin-inquiry-loading">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className="admin-inquiry-container">
      {/* Back to Dashboard Button */}
      <div className="admin-inquiry-header">
        <button
          className="admin-inquiry-back-btn"
          onClick={() => navigate("/admin/dashboard")}
        >
          <i className="fas fa-arrow-left"></i> Back to Dashboard
        </button>
      </div>

      {/* Title Section */}
      <div className="admin-inquiry-title">
        <h1>Inquiry Management</h1>
      </div>

      {/* Filter Section */}
      <div className="admin-inquiry-filter">
        <div className="admin-inquiry-filter-group">
          <label>Search</label>
          <input
            type="text"
            placeholder="Search by name or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="admin-inquiry-filter-group">
          <label>Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="new">New</option>
            <option value="closed">Closed</option>
          </select>
        </div>
        <div className="admin-inquiry-filter-group">
          <label>Date</label>
          <div style={{ position: 'relative' }}>
            <input
              style={{ width: "100%" }}
              value={`${format(range[0].startDate, 'yyyy-MMM-dd')}    to   ${format(range[0].endDate, 'yyyy-MMM-dd')}`}
              onClick={() => setShowPicker(!showPicker)}
              readOnly
            />
            {showPicker && (
              <div style={{
                position: 'absolute',
                zIndex: 10,
                top: '100%',
                right: 0,
                background: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }}>
                <DateRangePicker
                  locale={enUS}
                  editableDateInputs={true}
                  onChange={item => setRange([item.selection])}
                  moveRangeOnFirstSelection={false}
                  ranges={range}
                />
              </div>
            )}
          </div>
        </div>
        <button className="admin-inquiry-filter-btn" onClick={applyFilters}>
          Search
        </button>
      </div>

      {/* Table Container Section */}
      <div className="admin-inquiry-table-container">
        {currentInquiries.length > 0 ? (
          <table className="admin-inquiry-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Company</th>
                <th>Product</th>
                <th>Status</th>
                <th>Submitted Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentInquiries.map((inquiry) => (
                <tr key={inquiry.id}>
                  <td>{inquiry.name}</td>
                  <td>{inquiry.companyName || "N/A"}</td>
                  <td>
                    {inquiry.productName ||
                      `Product #${inquiry.productId}` ||
                      "N/A"}
                  </td>
                  <td>
                    <span
                      className={`admin-inquiry-status ${getStatusClass(
                        inquiry.status
                      )}`}
                    >
                      {inquiry.status}
                    </span>
                  </td>
                  <td>{formatDate(inquiry.submittedDate)}</td>
                  <td>
                    <button
                      className="admin-inquiry-view-btn"
                      onClick={() => handleViewInquiry(inquiry.id)}
                    >
                      <i className="fas fa-eye"></i> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="admin-inquiry-empty">
            <i className="fas fa-question-circle"></i>
            <p className="primary-message">
              No inquiries found matching your criteria.
            </p>
            <p className="secondary-message">
              The default filters will reflect the new status and current day's data. To view
              all inquiries, please adjust the filters and click apply.
            </p>
          </div>
        )}
      </div>

      {/* Card View Section */}
      <div className="admin-inquiry-card-list">
        {currentInquiries.length > 0 ? (
          currentInquiries.map((inquiry) => (
            <div key={inquiry.id} className="admin-inquiry-card">
              <div className="admin-inquiry-card-header">
                <div>
                  <div className="admin-inquiry-card-title">{inquiry.name}</div>
                  <div className="admin-inquiry-card-company">
                    {inquiry.companyName || "No Company"}
                  </div>
                  <div className="admin-inquiry-card-product">
                    <i className="fas fa-box"></i>{" "}
                    {inquiry.productName ||
                      `Product #${inquiry.productId}` ||
                      "N/A"}
                  </div>
                  <div className="admin-inquiry-card-date">
                    {formatDate(inquiry.submittedDate)}
                  </div>
                </div>
                <span
                  className={`admin-inquiry-status ${getStatusClass(
                    inquiry.status
                  )}`}
                >
                  {inquiry.status}
                </span>
              </div>
              <div className="admin-inquiry-card-footer">
                {/* <div className="admin-inquiry-card-email">{inquiry.email}</div> */}
                <button
                  className="admin-inquiry-view-btn"
                  onClick={() => handleViewInquiry(inquiry.id)}
                >
                  <i className="fas fa-eye"></i> View
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="admin-inquiry-empty">
            <i className="fas fa-question-circle"></i>
            <p className="primary-message">
              No inquiries found matching your criteria.
            </p>
            <p className="secondary-message">
              The default filters will reflect the new status and current day's data. To view
              all inquiries, please adjust the filters and click apply.
            </p>
            <button className="admin-inquiry-reset-btn" onClick={applyFilters}>
              <i className="fas fa-sync-alt"></i> Search
            </button>
          </div>
        )}
      </div>

      {/* Pagination */}
      {filteredInquiries.length > inquiriesPerPage && (
        <div className="admin-inquiry-pagination">
          <button
            className="admin-inquiry-pagination-btn"
            onClick={() => paginate(1)}
            disabled={currentPage === 1}
          >
            <i className="fas fa-angle-double-left"></i>
          </button>
          <button
            className="admin-inquiry-pagination-btn"
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <i className="fas fa-angle-left"></i>
          </button>

          {[...Array(totalPages).keys()].map((number) => {
            if (
              number + 1 === 1 ||
              number + 1 === totalPages ||
              (number + 1 >= currentPage - 1 && number + 1 <= currentPage + 1)
            ) {
              return (
                <button
                  key={number + 1}
                  className={`admin-inquiry-pagination-btn ${currentPage === number + 1 ? "active" : ""
                    }`}
                  onClick={() => paginate(number + 1)}
                >
                  {number + 1}
                </button>
              );
            }

            if (
              (number + 1 === currentPage - 2 && currentPage > 3) ||
              (number + 1 === currentPage + 2 && currentPage < totalPages - 2)
            ) {
              return (
                <span key={number + 1} className="admin-inquiry-pagination-btn">
                  ...
                </span>
              );
            }
            return null;
          })}

          <button
            className="admin-inquiry-pagination-btn"
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <i className="fas fa-angle-right"></i>
          </button>
          <button
            className="admin-inquiry-pagination-btn"
            onClick={() => paginate(totalPages)}
            disabled={currentPage === totalPages}
          >
            <i className="fas fa-angle-double-right"></i>
          </button>
        </div>

      )}
      {/* Inquiry details Popup */}
      {showModal && (
        <div className="admin-inquiry-modal-overlay" onClick={handleCloseModal}>
          <div
            className="admin-inquiry-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="admin-inquiry-modal-header">
              <div className="admin-inquiry-modal-title-container">
                <h2>Inquiry Details</h2>
                <div className="admin-inquiry-modal-id">
                  Inquiry #{inquiryDetails?.id}
                </div>
              </div>
              <button
                className="admin-inquiry-modal-close"
                onClick={handleCloseModal}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {modalLoading ? (
              <div className="admin-inquiry-detail-loading">
                <Loading />
              </div>
            ) : inquiryDetails ? (
              <div className="admin-inquiry-modal-content">
                <div className="admin-inquiry-detail-content">
                  {/* Inquiry Information Card */}
                  <div className="admin-inquiry-detail-card">
                    <h2 className="admin-inquiry-detail-card-title">
                      <i className="fas fa-info-circle"></i> Inquiry Information
                    </h2>

                    <div className="admin-inquiry-detail-info-grid">
                      <div className="admin-inquiry-detail-info-item">
                        <span className="admin-inquiry-detail-label">
                          Status
                        </span>
                        <span
                          className={`admin-inquiry-detail-status ${getStatusClass(
                            inquiryDetails.status
                          )}`}
                        >
                          {inquiryDetails.status}
                        </span>
                      </div>

                      <div className="admin-inquiry-detail-info-item">
                        <span className="admin-inquiry-detail-label">
                          Submitted Date
                        </span>
                        <span className="admin-inquiry-detail-value">
                          {formatDate(inquiryDetails.submittedDate)}
                        </span>
                      </div>

                      <div className="admin-inquiry-detail-info-item">
                        <span className="admin-inquiry-detail-label">Name</span>
                        <span className="admin-inquiry-detail-value">
                          {inquiryDetails.name}
                        </span>
                      </div>

                      <div className="admin-inquiry-detail-info-item">
                        <span className="admin-inquiry-detail-label">
                          Company
                        </span>
                        <span className="admin-inquiry-detail-value">
                          {inquiryDetails.companyName || "N/A"}
                        </span>
                      </div>

                      <div className="admin-inquiry-detail-info-item">
                        <span className="admin-inquiry-detail-label">
                          Phone
                        </span>
                        <span className="admin-inquiry-detail-value">
                          {inquiryDetails.phoneNumber || "N/A"}
                        </span>
                      </div>

                      <div className="admin-inquiry-detail-info-item">
                        <span className="admin-inquiry-detail-label">
                          Quantity
                        </span>
                        <span className="admin-inquiry-detail-value">
                          {inquiryDetails.quantity || "N/A"}
                        </span>
                      </div>

                      <div className="admin-inquiry-detail-info-item">
                        <span className="admin-inquiry-detail-label">
                          Delivery Location
                        </span>
                        <span className="admin-inquiry-detail-value">
                          {inquiryDetails.deliveryLocation || "N/A"}
                        </span>
                      </div>

                      <div className="admin-inquiry-detail-info-item">
                        <span className="admin-inquiry-detail-label">
                          Product ID
                        </span>
                        <span className="admin-inquiry-detail-value">
                          {inquiryDetails.productId || "N/A"}
                        </span>
                      </div>

                      <div className="admin-inquiry-detail-info-item">
                        <span className="admin-inquiry-detail-label">
                          Product Name
                        </span>
                        <span className="admin-inquiry-detail-value">
                          {inquiryDetails.productName || "Loading..."}
                        </span>
                      </div>

                      <div className="admin-inquiry-detail-info-item full-width">
                        <span className="admin-inquiry-detail-label">
                          Message
                        </span>
                        <div className="admin-inquiry-detail-message">
                          {inquiryDetails.message || "No message provided."}
                        </div>
                      </div>

                      {inquiryDetails.fileName && (
                        <div className="admin-inquiry-detail-info-item full-width">
                          <span className="admin-inquiry-detail-label">
                            Attachment
                          </span>
                          <div className="admin-inquiry-detail-attachment">
                            <a
                              href={`${inquiryDetails.attachmentUrl}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="admin-inquiry-detail-attachment-link"
                              download={inquiryDetails.fileName}
                            >
                              <i className="fas fa-paperclip"></i> Download{" "}
                              {inquiryDetails.fileName} (
                              {formatFileSize(inquiryDetails.fileSize)})
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Remark Section */}
                  <div className="admin-inquiry-detail-card">
                    <h2 className="admin-inquiry-detail-card-title">
                      <i className="fas fa-reply"></i> Remark
                    </h2>

                    <div className="admin-inquiry-detail-status-update">
                      {/* <div className="admin-inquiry-detail-status-update-title">
                        <i className="fas fa-reply"></i> Respond to Inquiry
                      </div> */}

                      <div className="admin-inquiry-detail-form-group">
                        <label className="admin-inquiry-detail-label">
                          Remark Message:
                        </label>
                        <textarea
                          className="admin-inquiry-detail-textarea"
                          value={responseMessage}
                          onChange={(e) => setResponseMessage(e.target.value)}
                          placeholder="Enter your remark to this inquiry..."
                          disabled={updatingStatus}
                        />
                      </div>

                      <button
                        className="admin-inquiry-detail-btn-action admin-inquiry-detail-btn-primary"
                        onClick={() => handleSubmitResponse(false)}
                        disabled={updatingStatus || !responseMessage.trim()}
                      >
                        {updatingStatus ? (
                          <>
                            <i className="fas fa-spinner fa-spin"></i>{" "}
                            Sending...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-paper-plane"></i> Add Remark
                          </>
                        )}
                      </button>
                    </div>

                    {/* Remark History */}
                    {inquiryDetails.responses &&
                      inquiryDetails.responses.length > 0 ? (
                      <div className="admin-inquiry-detail-responses">
                        <h3 className="admin-inquiry-detail-card-title">
                          <i className="fas fa-history"></i> Remark History
                        </h3>

                        {inquiryDetails.responses.map((response) => (
                          <div
                            key={response.id}
                            className="admin-inquiry-detail-response"
                          >
                            <div className="admin-inquiry-detail-response-header">
                              <span className="admin-inquiry-detail-response-user">
                                {response.respondedBy || "Admin"}
                              </span>
                              <span className="admin-inquiry-detail-response-date">
                                {formatDate(response.responseDate)}
                              </span>
                            </div>
                            <div className="admin-inquiry-detail-response-content">
                              {response.message}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="admin-inquiry-detail-empty-responses">
                        <p>No responses yet.</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer Section */}
                <div className="admin-inquiry-detail-actions">
                  <button
                    type="button"
                    className="admin-inquiry-btn-cancel"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  {inquiryDetails.status !== "Closed" && (
                    <button
                      className="admin-inquiry-detail-btn-action admin-inquiry-detail-btn-danger"
                      onClick={() => {
                        const hasResponses =
                          inquiryDetails.responses &&
                          inquiryDetails.responses.length > 0;

                        if (!hasResponses) {
                          MySwal.fire({
                            title: "Cannot Close Inquiry",
                            text: "You must add at least one remark before closing the inquiry.",
                            icon: "warning",
                            confirmButtonColor: "#45a049",
                          });
                          return;
                        }

                        MySwal.fire({
                          title: "Are you sure?",
                          text: "You are about to set the status to close for this inquiry.",
                          icon: "warning",
                          showCancelButton: true,
                          confirmButtonColor: "#45a049",
                          cancelButtonColor: "#7a7a7a",
                          confirmButtonText: "Yes",
                        }).then((result) => {
                          if (result.isConfirmed) {
                            const closeMessage =
                              "This inquiry has been closed.";

                            const submitCloseInquiry = async () => {
                              if (!inquiryDetails) return;

                              setUpdatingStatus(true);
                              try {
                                const responseData = {
                                  inquiryId: parseInt(inquiryDetails.id),
                                  message: closeMessage,
                                  respondedBy: userInfo?.username || "Admin",
                                };

                                const responseResult = await authFetch(
                                  `${API_BASE_URL}/api/InquiryResponses`,
                                  {
                                    method: "POST",
                                    headers: {
                                      "Content-Type": "application/json",
                                      "X-Update-Status": "Closed",
                                    },
                                    body: JSON.stringify(responseData),
                                  }
                                );

                                if (!responseResult.ok) {
                                  throw new Error(
                                    `Failed to submit response: ${responseResult.status}`
                                  );
                                }

                                const inquiryResponse = await authFetch(
                                  `${API_BASE_URL}/Inquiries/${selectedInquiry}`
                                );
                                if (!inquiryResponse.ok) {
                                  throw new Error(
                                    `Failed to refresh inquiry: ${inquiryResponse.status}`
                                  );
                                }

                                const updatedInquiry =
                                  await inquiryResponse.json();
                                const responsesResponse = await authFetch(
                                  `${API_BASE_URL}/api/InquiryResponses/inquiry/${selectedInquiry}`
                                );

                                if (responsesResponse.ok) {
                                  const responses =
                                    await responsesResponse.json();
                                  updatedInquiry.responses = responses;
                                }

                                setInquiryDetails(updatedInquiry);
                                setResponseMessage("");

                                const updatedInquiries = inquiries.map(
                                  (inquiry) =>
                                    inquiry.id === selectedInquiry
                                      ? { ...inquiry, status: "Closed" }
                                      : inquiry
                                );
                                setInquiries(updatedInquiries);

                                MySwal.fire({
                                  title: "Success!",
                                  text: "Inquiry closed successfully!",
                                  icon: "success",
                                  confirmButtonColor: "#45a049",
                                });
                              } catch (err) {
                                MySwal.fire({
                                  title: "Error!",
                                  text: "Failed to close inquiry. Please try again.",
                                  icon: "error",
                                  confirmButtonColor: "#45a049",
                                });
                              } finally {
                                setUpdatingStatus(false);
                              }
                            };

                            submitCloseInquiry();
                          }
                        });
                      }}
                      disabled={updatingStatus}
                    >
                      Close Inquiry
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="admin-inquiry-detail-error">
                <p>Could not load inquiry details. Please try again.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminInquiries;
